"use client";

// Integrated documentation view: three-column docs layout (nav / article / TOC)
// rendered with the app's own design tokens. Language follows the app locale;
// German falls back to English (docs ship EN + FR).

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useI18n } from "@/lib/i18n/provider";
import { Button } from "@/components/ui/button";
import { BookOpen, Printer } from "lucide-react";
import { sectionsEN } from "./content-en";
import { sectionsFR } from "./content-fr";
import { AccessLayers } from "./access-layers";
import type { DocSection } from "./primitives";

const PART_LABELS: Record<1 | 2 | 3, { en: string; fr: string }> = {
  1: { en: "Part 1 · Start here", fr: "Partie 1 · Commencez ici" },
  2: { en: "Part 2 · The main flow", fr: "Partie 2 · Le parcours principal" },
  3: { en: "Part 3 · Owners, helpers & words", fr: "Partie 3 · Propriétaires, aides & mots" },
};

const INTRO = {
  en: {
    h1: "HomeBook Documentation",
    lede: "A shared family calendar for our houses — how to sign in, book a stay, read the calendar, and sort out the bills. Written for normal humans.",
    meta: "https://homebook.freeboxos.fr · UI languages: EN · FR · DE",
    onThisPage: "On this page",
    printable: "Printable versions",
    tocLabel: "On this page",
    footer:
      "HomeBook · documentation · printable copies: /documentation-en.html · /documentation-fr.html",
  },
  fr: {
    h1: "Documentation HomeBook",
    lede: "Un calendrier familial partagé pour nos maisons — se connecter, réserver un séjour, lire le calendrier et régler les factures. Écrit pour des humains normaux.",
    meta: "https://homebook.freeboxos.fr · Langues de l'interface : EN · FR · DE",
    onThisPage: "Sur cette page",
    printable: "Versions imprimables",
    tocLabel: "Sur cette page",
    footer:
      "HomeBook · documentation · copies imprimables : /documentation-en.html · /documentation-fr.html",
  },
};

export function DocumentationView() {
  const { locale } = useI18n();
  const lang = locale === "fr" ? "fr" : "en"; // de falls back to en
  const sections: DocSection[] = lang === "fr" ? sectionsFR : sectionsEN;
  const t = INTRO[lang as "en" | "fr"];

  const [active, setActive] = useState(sections[0]?.id ?? "");

  // Rebuild the scroll-spy whenever the language (and thus the DOM) changes.
  useEffect(() => {
    const headings = Array.from(
      document.querySelectorAll<HTMLElement>("#doc-article h2[data-doc-id]")
    );
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) setActive(e.target.id);
      },
      { rootMargin: "-15% 0px -70% 0px" }
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [lang]);

  const grouped = useMemo(() => {
    const parts: (1 | 2 | 3)[] = [1, 2, 3];
    return parts.map((p) => ({
      part: p,
      label: PART_LABELS[p][lang as "en" | "fr"],
      items: sections.filter((s) => s.part === p),
    }));
  }, [sections, lang]);

  const navLink = (s: DocSection, withNum: boolean) => (
    <a
      key={s.id}
      href={`#${s.id}`}
      className={`block rounded-md px-2.5 py-1 text-[13px] leading-snug no-underline transition-colors ${
        active === s.id
          ? "bg-primary/10 font-medium text-primary shadow-[inset_3px_0_0_var(--primary)]"
          : "text-foreground/80 hover:bg-muted"
      }`}
    >
      {withNum && <span className="me-2 text-[11px] text-muted-foreground">{s.num}</span>}
      {s.title}
    </a>
  );

  let lastPart: 1 | 2 | 3 | null = null;

  return (
    <div className="mx-auto w-full max-w-[1400px]">
      {/* Mobile section jump list */}
      <details className="mx-4 mt-4 rounded-lg border bg-card p-3 lg:hidden">
        <summary className="cursor-pointer text-sm font-medium">
          {t.tocLabel}
        </summary>
        <div className="mt-2 space-y-4">
          {grouped.map((g) => (
            <div key={g.part}>
              <div className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {g.label}
              </div>
              {g.items.map((s) => navLink(s, true))}
            </div>
          ))}
        </div>
      </details>

      <div className="lg:grid lg:grid-cols-[250px_minmax(0,1fr)] xl:grid-cols-[250px_minmax(0,1fr)_230px]">
        {/* Inline-start nav */}
        <nav className="hidden lg:block" aria-label="Documentation sections">
          <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto py-8 pe-3">
            {grouped.map((g) => (
              <div key={g.part} className="mb-6">
                <div className="px-2.5 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {g.label}
                </div>
                {g.items.map((s) => navLink(s, true))}
              </div>
            ))}
          </div>
        </nav>

        {/* Article */}
        <article
          id="doc-article"
          className="min-w-0 max-w-[780px] px-4 py-8 sm:px-6 lg:px-10 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]"
        >
          <h1 className="text-4xl font-bold tracking-tight">{t.h1}</h1>
          <p className="mt-3 max-w-[70ch] text-lg leading-relaxed text-muted-foreground">
            {t.lede}
          </p>
          <p className="mt-2 text-[13px] text-muted-foreground">{t.meta}</p>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Button asChild size="sm" variant="outline" className="gap-2">
              <Link href="/">
                <BookOpen className="h-4 w-4" />
                {lang === "fr" ? "Retour à l'application" : "Back to the app"}
              </Link>
            </Button>
            <Button asChild size="sm" variant="ghost" className="gap-2">
              <a
                href={lang === "fr" ? "/documentation-fr.html" : "/documentation-en.html"}
                target="_blank"
                rel="noreferrer"
              >
                <Printer className="h-4 w-4" />
                {t.printable}
              </a>
            </Button>
          </div>

          {/* Access-model layers diagram */}
          <div className="mt-8 flex justify-center">
            <div className="w-full max-w-2xl rounded-2xl border bg-card p-3 shadow-sm sm:p-4">
              <AccessLayers />
            </div>
          </div>

          {sections.map((s) => {
            const showKicker = s.part !== lastPart;
            lastPart = s.part;
            return (
              <section key={s.id}>
                {showKicker && (
                  <p className="mb-1 mt-12 text-[11px] font-semibold uppercase tracking-wider text-primary">
                    {PART_LABELS[s.part][lang as "en" | "fr"]}
                  </p>
                )}
                <h2
                  id={s.id}
                  data-doc-id={s.id}
                  className="mt-3 scroll-mt-24 text-2xl font-semibold tracking-tight"
                >
                  <span className="me-3 text-lg text-primary">{s.num}</span>
                  {s.title}
                </h2>
                {s.body}
              </section>
            );
          })}

          <p className="mt-14 border-t pt-4 text-[12.5px] text-muted-foreground">
            {t.footer}
          </p>
        </article>

        {/* Inline-end TOC */}
        <aside className="hidden xl:block" aria-label={t.onThisPage}>
          <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto py-8 ps-4 text-[13px]">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t.tocLabel}
            </div>
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={`block py-1 leading-snug no-underline transition-colors ${
                  active === s.id
                    ? "font-medium text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {s.num} {s.title}
              </a>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
